import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Loader2, User, Calendar, ArrowRight, Check, Clock, Building2, Phone, Mail, FileText, Sparkles, Brain, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// D3 Command Logo Icon component
const D3LogoIcon = ({ className = '', size = 24 }) => (
  <img
    src="/favicon.png"
    alt="D3"
    className={className}
    style={{ width: `${size}px`, height: `${size}px`, objectFit: 'contain' }}
    draggable={false}
  />
);

// Lead capture qualification tracking (V2.0)
// These keywords indicate readiness for multi-turn qualification
const QUALIFICATION_TRIGGERS = [
  'pricing', 'price', 'cost', 'quote', 'estimate', 'budget',
  'how much', 'what does it cost', 'what will it cost',
  'demo', 'demonstration',
  'consultation', 'consult', 'meeting', 'discovery call',
  'speak with', 'talk to', 'contact', 'reach out',
  'get started', 'how to start', 'next steps',
  'timeline', 'how long', 'when can',
  'i want to build', 'i need to build', 'can you build',
  'i want you to', 'i need you to'
];

// V2.1: Human escalation triggers
const HUMAN_ESCALATION_TRIGGERS = [
  'talk to someone', 'speak with someone', 'speak to someone',
  'human', 'real person', 'actual person',
  'call me', 'phone call', 'schedule a call',
  'speak with sales', 'talk to sales', 'sales team',
  'schedule a meeting', 'book a meeting', 'set up a meeting',
  'contact your team', 'reach out to your team', 'connect me with your team',
  'get in touch', 'contact someone'
];

// V2.1: Service Explorer options (for "I don't know what I need")
const SERVICE_EXPLORER = [
  { id: 'ai', emoji: '🤖', label: 'AI Applications', description: 'Intelligent automation & AI-powered solutions', message: "Tell me about AI applications and how they can help my business." },
  { id: 'automation', emoji: '⚙️', label: 'Workflow Automation', description: 'Streamline processes & boost efficiency', message: "I want to learn about workflow automation solutions." },
  { id: 'software', emoji: '🌐', label: 'Custom Software', description: 'Tailored solutions for your needs', message: "Tell me about custom software development." },
  { id: 'mobile', emoji: '📱', label: 'Mobile Apps', description: 'iOS & Android applications', message: "I&apos;m interested in mobile app development." },
  { id: 'cloud', emoji: '☁️', label: 'Cloud Solutions', description: 'Scalable cloud infrastructure & apps', message: "Tell me about cloud solutions and deployment." },
  { id: 'government', emoji: '🏛', label: 'Government Solutions', description: 'Secure public sector technology', message: "I&apos;m looking for government technology solutions." },
];

// Check if message indicates qualification should begin
const shouldStartQualification = (message) => {
  const lower = message.toLowerCase();
  return QUALIFICATION_TRIGGERS.some(trigger => lower.includes(trigger));
};

// V2.1: Check if message requests human escalation
const shouldEscalateToHuman = (message) => {
  const lower = message.toLowerCase();
  return HUMAN_ESCALATION_TRIGGERS.some(trigger => lower.includes(trigger));
};

// V2.1: Check if visitor doesn't know what they need
const shouldShowServiceExplorer = (message) => {
  const lower = message.toLowerCase();
  return (lower.includes("don't know") || lower.includes("dont know") || lower.includes("not sure")) &&
         (lower.includes("what i need") || lower.includes("which") || lower.includes("help me choose"));
};

// Quick action buttons configuration
const QUICK_ACTIONS = [
  { id: 'ai-app', emoji: '🚀', label: 'Build an AI App', message: "I'm interested in building an AI-powered application for my business." },
  { id: 'software', emoji: '💻', label: 'Custom Software', message: "I need custom software development for my organization." },
  { id: 'automation', emoji: '⚙️', label: 'Workflow Automation', message: "I want to automate workflows and business processes." },
  { id: 'government', emoji: '🏛', label: 'Government Solutions', message: "I'm looking for government or public sector technology solutions." },
  { id: 'consultation', emoji: '📅', label: 'Book a Consultation', message: "I'd like to schedule a consultation to discuss my project." },
  { id: 'quote', emoji: '💲', label: 'Request a Quote', message: "I'd like to request a quote for a project." },
];

// Format timestamp
const formatTime = (date) => {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(date);
};

// Message component with animations and streaming support
const ChatMessage = ({ message, isTyping = false, isStreaming = false }) => {
  const isUser = message.role === 'user';
  
  return (
    <div 
      className={`flex gap-3 animate-fadeSlideUp ${isUser ? 'flex-row-reverse' : ''}`}
      role="article"
      aria-label={`${isUser ? 'You' : 'D3 Command AI'} said`}
    >
      {/* Avatar */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        isUser ? 'bg-indigo-600' : 'bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-white/10'
      }`}>
        {isUser ? (
          <User className="w-4 h-4 text-white" />
        ) : (
          <D3LogoIcon size={20} />
        )}
      </div>
      
      {/* Message content */}
      <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} max-w-[80%]`}>
        <div
          className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${
            isUser
              ? 'bg-indigo-600 text-white rounded-br-md'
              : 'bg-white/[0.03] border border-white/[0.06] text-gray-200 rounded-bl-md'
          }`}
        >
          {isTyping ? (
            <div className="flex items-center gap-2">
              <Brain className="w-3.5 h-3.5 text-blue-400 animate-pulse" />
              <span className="text-gray-400 text-xs">Analyzing your request...</span>
            </div>
          ) : (
            <div className="whitespace-pre-wrap">
              {message.content}
              {isStreaming && <span className="inline-block w-1.5 h-4 bg-blue-400 ml-0.5 animate-pulse" />}
            </div>
          )}
        </div>
        {message.timestamp && !isTyping && !isStreaming && (
          <span className="text-[10px] text-gray-500 mt-1 px-1">
            {formatTime(new Date(message.timestamp))}
          </span>
        )}
      </div>
    </div>
  );
};

// Quick action buttons component
const QuickActions = ({ onSelect, disabled }) => (
  <div className="space-y-3 animate-fadeSlideUp">
    <p className="text-xs text-gray-500 px-1">Quick actions</p>
    <div className="grid grid-cols-2 gap-2">
      {QUICK_ACTIONS.map((action) => (
        <button
          key={action.id}
          onClick={() => onSelect(action.message)}
          disabled={disabled}
          className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-blue-500/30 hover:bg-white/[0.04] text-gray-300 text-xs font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
          aria-label={action.label}
        >
          <span className="text-base">{action.emoji}</span>
          <span className="truncate group-hover:text-white transition-colors">{action.label}</span>
        </button>
      ))}
    </div>
  </div>
);

// Enhanced lead capture form
const LeadCaptureForm = ({ sessionId, onSubmit, onCancel }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    project_type: '',
    description: '',
    timeline: '',
    budget: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step === 1) {
      setStep(2);
      return;
    }
    
    setError('');
    setSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/chat/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, session_id: sessionId })
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to submit');
      }

      onSubmit();
    } catch (err) {
      setError(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="animate-fadeSlideUp">
      <div className="p-4 bg-gradient-to-br from-blue-500/[0.08] to-indigo-500/[0.08] border border-blue-500/20 rounded-2xl">
        {/* Progress indicator */}
        <div className="flex items-center gap-2 mb-4">
          <div className={`w-2 h-2 rounded-full ${step >= 1 ? 'bg-blue-400' : 'bg-gray-600'}`} />
          <div className={`flex-1 h-0.5 ${step >= 2 ? 'bg-blue-400' : 'bg-gray-600'}`} />
          <div className={`w-2 h-2 rounded-full ${step >= 2 ? 'bg-blue-400' : 'bg-gray-600'}`} />
        </div>
        
        <h4 className="text-white font-semibold text-sm mb-1 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-blue-400" />
          {step === 1 ? 'Contact Information' : 'Project Details'}
        </h4>
        <p className="text-gray-400 text-xs mb-4">
          {step === 1 
            ? "We'll use this to prepare a personalized recommendation." 
            : "Help us understand your project better."}
        </p>
        
        {error && (
          <div className="mb-3 p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-3">
          {step === 1 ? (
            <>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  required
                  placeholder="Your name *"
                  value={form.name}
                  onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/10 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-blue-500/40 transition-colors"
                  aria-label="Your name"
                />
              </div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="email"
                  required
                  placeholder="Work email *"
                  value={form.email}
                  onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/10 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-blue-500/40 transition-colors"
                  aria-label="Work email"
                />
              </div>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Company (optional)"
                  value={form.company}
                  onChange={(e) => setForm(p => ({ ...p, company: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/10 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-blue-500/40 transition-colors"
                  aria-label="Company"
                />
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={form.phone}
                  onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/10 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-blue-500/40 transition-colors"
                  aria-label="Phone number"
                />
              </div>
            </>
          ) : (
            <>
              <select
                value={form.project_type}
                onChange={(e) => setForm(p => ({ ...p, project_type: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500/40 transition-colors appearance-none cursor-pointer"
                aria-label="Project type"
              >
                <option value="" className="bg-[#0A0A0B]">Select project type</option>
                <option value="AI Application" className="bg-[#0A0A0B]">AI Application</option>
                <option value="Custom Software" className="bg-[#0A0A0B]">Custom Software</option>
                <option value="Web Application" className="bg-[#0A0A0B]">Web Application</option>
                <option value="Mobile App" className="bg-[#0A0A0B]">Mobile App</option>
                <option value="Workflow Automation" className="bg-[#0A0A0B]">Workflow Automation</option>
                <option value="API Integration" className="bg-[#0A0A0B]">API Integration</option>
                <option value="Government Solution" className="bg-[#0A0A0B]">Government Solution</option>
                <option value="Platform Demo" className="bg-[#0A0A0B]">Platform Demo</option>
                <option value="Consultation" className="bg-[#0A0A0B]">Consultation Only</option>
                <option value="Other" className="bg-[#0A0A0B]">Other</option>
              </select>
              <textarea
                placeholder="Tell us about your project..."
                value={form.description}
                onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/10 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-blue-500/40 transition-colors resize-none"
                aria-label="Project description"
              />
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={form.timeline}
                  onChange={(e) => setForm(p => ({ ...p, timeline: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500/40 transition-colors appearance-none cursor-pointer"
                  aria-label="Expected timeline"
                >
                  <option value="" className="bg-[#0A0A0B]">Timeline</option>
                  <option value="ASAP" className="bg-[#0A0A0B]">ASAP</option>
                  <option value="1-2 weeks" className="bg-[#0A0A0B]">1-2 weeks</option>
                  <option value="1 month" className="bg-[#0A0A0B]">1 month</option>
                  <option value="2-3 months" className="bg-[#0A0A0B]">2-3 months</option>
                  <option value="Flexible" className="bg-[#0A0A0B]">Flexible</option>
                </select>
                <select
                  value={form.budget}
                  onChange={(e) => setForm(p => ({ ...p, budget: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500/40 transition-colors appearance-none cursor-pointer"
                  aria-label="Budget range"
                >
                  <option value="" className="bg-[#0A0A0B]">Budget</option>
                  <option value="Under $10k" className="bg-[#0A0A0B]">Under $10k</option>
                  <option value="$10k-$25k" className="bg-[#0A0A0B]">$10k-$25k</option>
                  <option value="$25k-$50k" className="bg-[#0A0A0B]">$25k-$50k</option>
                  <option value="$50k-$100k" className="bg-[#0A0A0B]">$50k-$100k</option>
                  <option value="$100k+" className="bg-[#0A0A0B]">$100k+</option>
                  <option value="Not sure" className="bg-[#0A0A0B]">Not sure</option>
                </select>
              </div>
            </>
          )}
          
          <div className="flex gap-2 pt-1">
            {step === 2 && (
              <button
                type="button"
                onClick={() => setStep(1)}
                className="px-4 py-2.5 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] text-gray-400 text-sm transition-colors"
              >
                Back
              </button>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-50 text-white text-sm font-medium transition-all flex items-center justify-center gap-2"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : step === 1 ? (
                <>Continue <ArrowRight className="w-4 h-4" /></>
              ) : (
                <>Submit <Check className="w-4 h-4" /></>
              )}
            </button>
          </div>
          
          <button
            type="button"
            onClick={onCancel}
            className="w-full text-center text-xs text-gray-500 hover:text-gray-400 transition-colors py-1"
          >
            I&apos;ll do this later
          </button>
        </form>
      </div>
    </div>
  );
};

// V2.1: Suggested questions component (parses AI response for suggestions)
const SuggestedQuestions = ({ message, onSelect, disabled }) => {
  // Extract suggested questions from message content
  const extractSuggestions = (content) => {
    const lines = content.split('\n');
    const suggestions = [];
    let inSuggestionsSection = false;
    
    for (const line of lines) {
      if (line.includes('You might also want to know:') || line.includes('**You might also want to know:**')) {
        inSuggestionsSection = true;
        continue;
      }
      if (inSuggestionsSection && line.match(/^\d+\./)) {
        const suggestion = line.replace(/^\d+\.\s*/, '').trim();
        if (suggestion) suggestions.push(suggestion);
      }
    }
    
    return suggestions.slice(0, 3); // Max 3 suggestions
  };

  const suggestions = message.role === 'assistant' ? extractSuggestions(message.content) : [];
  
  if (suggestions.length === 0) return null;

  return (
    <div className="animate-fadeSlideUp space-y-2 mt-2">
      <p className="text-[10px] text-gray-500 px-1">Suggested questions</p>
      <div className="flex flex-col gap-1.5">
        {suggestions.map((suggestion, idx) => (
          <button
            key={idx}
            onClick={() => onSelect(suggestion)}
            disabled={disabled}
            className="text-left px-3 py-2 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:border-blue-500/30 hover:bg-white/[0.04] text-gray-300 text-xs transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            <span className="group-hover:text-white transition-colors">{suggestion}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// V2.1: Service Explorer component (for "I don't know what I need")
const ServiceExplorer = ({ onSelect, disabled }) => (
  <div className="animate-fadeSlideUp space-y-3">
    <div className="text-center mb-2">
      <p className="text-sm text-gray-300 mb-1">Let me help you find the right solution.</p>
      <p className="text-xs text-gray-500">What area interests you most?</p>
    </div>
    <div className="grid grid-cols-2 gap-2">
      {SERVICE_EXPLORER.map((service) => (
        <button
          key={service.id}
          onClick={() => onSelect(service.message)}
          disabled={disabled}
          className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-blue-500/30 hover:bg-white/[0.04] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed group"
        >
          <span className="text-2xl">{service.emoji}</span>
          <span className="text-xs font-medium text-gray-300 group-hover:text-white transition-colors text-center">
            {service.label}
          </span>
          <span className="text-[10px] text-gray-500 text-center">
            {service.description}
          </span>
        </button>
      ))}
    </div>
  </div>
);

// V2.1: Exit Intent Modal
const ExitIntentModal = ({ onYes, onMaybeLater }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fadeIn">
    <div className="bg-[#0D1321] border border-white/[0.08] rounded-2xl p-6 max-w-sm mx-4 animate-scaleIn shadow-2xl">
      <div className="text-center mb-4">
        <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-3">
          <Calendar className="w-6 h-6 text-blue-400" />
        </div>
        <h4 className="text-white font-semibold text-lg mb-2">Before you go...</h4>
        <p className="text-gray-400 text-sm">
          Would you like a free consultation to discuss your project?
        </p>
      </div>
      <div className="flex gap-3">
        <button
          onClick={onYes}
          className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-sm font-medium transition-all"
        >
          Yes, please
        </button>
        <button
          onClick={onMaybeLater}
          className="flex-1 py-2.5 rounded-xl bg-white/[0.03] hover:bg-white/[0.06] text-gray-400 hover:text-white text-sm transition-all"
        >
          Maybe later
        </button>
      </div>
    </div>
  </div>
);

// V2.1: Premium success message after lead capture
const LeadCaptureSuccess = () => (
  <div className="animate-fadeSlideUp p-5 bg-gradient-to-br from-green-500/[0.08] to-emerald-500/[0.08] border border-green-500/20 rounded-2xl">
    <div className="text-center mb-4">
      <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
        <Check className="w-6 h-6 text-green-400" />
      </div>
      <h4 className="text-white font-semibold text-base mb-2">🎉 Thank you!</h4>
      <p className="text-gray-300 text-sm mb-2">
        Your consultation request has been received successfully.
      </p>
      <p className="text-gray-400 text-xs">
        One of our solution architects will review your project requirements and contact you shortly.
      </p>
    </div>
    <div className="text-center pt-3 border-t border-white/[0.06]">
      <p className="text-gray-400 text-xs mb-2">
        You&apos;re welcome to continue asking questions while you wait.
      </p>
    </div>
  </div>
);

// Main chatbot component (V2.1 with premium enhancements)
export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingMessageIndex, setStreamingMessageIndex] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [qualificationStarted, setQualificationStarted] = useState(false);
  const [qualificationTurnCount, setQualificationTurnCount] = useState(0);
  
  // V2.1: New state variables
  const [showServiceExplorer, setShowServiceExplorer] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [discussedPricing, setDiscussedPricing] = useState(false);
  const [isReturningVisitor, setIsReturningVisitor] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);

  // V2.1: Premium welcome message
  const welcomeMessage = {
    role: 'assistant',
    content: `👋 Welcome to D3 Command.

I'm your AI solutions advisor.

I can help you:
• Build AI-powered applications
• Design custom software
• Automate business processes
• Develop web & mobile apps
• Discuss enterprise technology
• Explore government technology solutions
• Estimate project scope

How can I help you today?`,
    timestamp: new Date().toISOString()
  };

  // Initialize
  useEffect(() => {
    const stored = sessionStorage.getItem('d3_chat_session');
    const previousMessages = sessionStorage.getItem('d3_chat_history');
    
    if (stored) {
      setSessionId(stored);
      // V2.1: Returning visitor detection
      if (previousMessages) {
        setIsReturningVisitor(true);
      }
    } else {
      const newId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('d3_chat_session', newId);
      setSessionId(newId);
    }
    
    // Set welcome message
    setMessages([welcomeMessage]);
  }, []);
  
  // V2.1: Save conversation history
  useEffect(() => {
    if (messages.length > 1) {
      sessionStorage.setItem('d3_chat_history', JSON.stringify(messages));
    }
  }, [messages]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, showLeadCapture, leadCaptured]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Streaming message handler
  const sendMessageWithStreaming = useCallback(async (messageText = null) => {
    const text = messageText || inputValue.trim();
    if (!text || isLoading || isStreaming) return;

    setInputValue('');
    setShowQuickActions(false);
    
    const userMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    
    // V2.1: Check for service explorer need
    if (shouldShowServiceExplorer(text)) {
      setShowServiceExplorer(true);
      setIsLoading(false);
      return; // Don't send to API, show service explorer instead
    }
    
    // V2.1: Check for human escalation
    const needsHumanEscalation = shouldEscalateToHuman(text);
    if (needsHumanEscalation) {
      setIsLoading(false);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'd be happy to connect you with our team.\n\nPlease provide your contact details and one of our solution advisors will reach out to discuss your project in detail.",
        timestamp: new Date().toISOString()
      }]);
      setTimeout(() => setShowLeadCapture(true), 800);
      return;
    }
    
    // V2.1: Track if discussing pricing/quote
    if (shouldStartQualification(text)) {
      setDiscussedPricing(true);
    }

    // Check if this might start qualification
    if (!qualificationStarted && shouldStartQualification(text)) {
      setQualificationStarted(true);
      setQualificationTurnCount(0);
    } else if (qualificationStarted) {
      setQualificationTurnCount(prev => prev + 1);
    }

    try {
      // Create abort controller for this request
      abortControllerRef.current = new AbortController();
      
      const res = await fetch(`${API_URL}/api/chat/message/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          session_id: sessionId
        }),
        signal: abortControllerRef.current.signal
      });

      if (!res.ok) throw new Error('Failed to get response');

      // Hide typing indicator and prepare for streaming
      setIsLoading(false);
      setIsStreaming(true);
      
      // Add empty assistant message that will be updated with streaming content
      const newMessageIndex = messages.length + 1;
      setStreamingMessageIndex(newMessageIndex);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString()
      }]);

      // Read the stream
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let previousDataLine = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              break;
            }
            
            // If we had a previous data line, it was part of multi-line response
            // Add newline before appending new line (except for first line)
            if (previousDataLine !== null && data) {
              accumulatedText += '\n';
            }
            
            accumulatedText += data;
            previousDataLine = data;
            
            // Update the streaming message
            setMessages(prev => {
              const newMessages = [...prev];
              newMessages[newMessageIndex] = {
                role: 'assistant',
                content: accumulatedText,
                timestamp: new Date().toISOString()
              };
              return newMessages;
            });
          }
        }
      }

      // Streaming complete
      setIsStreaming(false);
      setStreamingMessageIndex(null);

      // Check if we should show lead capture form after qualification
      // Show form after 2-3 turns of qualification conversation
      if (qualificationStarted && qualificationTurnCount >= 1 && !leadCaptured) {
        // Check if the assistant's response suggests contact collection
        // (looking for phrases like "may I collect" or "contact details" or "next step")
        const lastResponse = accumulatedText.toLowerCase();
        if (lastResponse.includes('contact') || lastResponse.includes('details') || 
            lastResponse.includes('information') || lastResponse.includes('may i collect')) {
          setTimeout(() => setShowLeadCapture(true), 800);
        }
      }

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request aborted');
        return;
      }
      
      setIsLoading(false);
      setIsStreaming(false);
      setStreamingMessageIndex(null);
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I&apos;m sorry, I&apos;m having trouble connecting right now. Please try again or contact us at support@d3command.com.",
        timestamp: new Date().toISOString()
      }]);
    }
  }, [inputValue, sessionId, isLoading, isStreaming, messages.length, qualificationStarted, qualificationTurnCount, leadCaptured]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessageWithStreaming();
    }
  };

  const handleQuickAction = (message) => {
    sendMessageWithStreaming(message);
  };

  const handleLeadSubmit = () => {
    setShowLeadCapture(false);
    setLeadCaptured(true);
    setQualificationStarted(false);
    setQualificationTurnCount(0);
  };

  return (
    <>
      {/* CSS for animations */}
      <style>{`
        @keyframes fadeSlideUp {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes breathe {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(79, 70, 229, 0.4);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(79, 70, 229, 0);
          }
        }
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fadeSlideUp {
          animation: fadeSlideUp 0.3s ease-out forwards;
        }
        .animate-breathe {
          animation: breathe 8s ease-in-out infinite;
        }
        .animate-scaleIn {
          animation: scaleIn 0.2s ease-out forwards;
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>

      {/* Floating chat button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-[58px] h-[58px] rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-lg shadow-indigo-500/20 flex items-center justify-center transition-all duration-300 hover:scale-105 ${
          isOpen ? 'scale-0 opacity-0 pointer-events-none' : 'scale-100 opacity-100 animate-breathe'
        }`}
        aria-label="Open chat assistant"
        aria-expanded={isOpen}
      >
        <D3LogoIcon size={30} />
      </button>

      {/* Chat window */}
      <div
        className={`fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-48px)] bg-[#0D1321] border border-white/[0.08] rounded-2xl shadow-2xl shadow-black/40 flex flex-col transition-all duration-300 origin-bottom-right ${
          isOpen ? 'animate-scaleIn' : 'scale-0 opacity-0 pointer-events-none'
        }`}
        style={{ height: '600px', maxHeight: 'calc(100vh - 100px)' }}
        role="dialog"
        aria-label="D3 Command AI Assistant"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-white/[0.06] bg-gradient-to-r from-[#0A0F1A] to-[#0D1321] rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-white/10 flex items-center justify-center">
              <D3LogoIcon size={24} />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                D3 Command AI
              </h3>
              <div className="flex items-center gap-1.5">
                <span className="flex items-center gap-1 text-green-400 text-[10px]">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  Online 24/7
                </span>
                <span className="text-gray-600">•</span>
                <span className="text-gray-500 text-[10px]">Helping businesses build smarter</span>
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              // V2.1: Exit intent - show modal if discussed pricing
              if (discussedPricing && !leadCaptured && messages.length > 2) {
                setShowExitIntent(true);
              } else {
                setIsOpen(false);
              }
            }}
            className="w-8 h-8 rounded-lg hover:bg-white/[0.04] flex items-center justify-center text-gray-400 hover:text-white transition-colors"
            aria-label="Close chat"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
          {messages.map((msg, i) => (
            <div key={i}>
              <ChatMessage 
                message={msg} 
                isStreaming={isStreaming && i === streamingMessageIndex}
              />
              {/* V2.1: Suggested questions after assistant messages */}
              {msg.role === 'assistant' && i === messages.length - 1 && !isStreaming && !isLoading && !leadCaptured && (
                <SuggestedQuestions 
                  message={msg} 
                  onSelect={handleQuickAction} 
                  disabled={isLoading || isStreaming} 
                />
              )}
            </div>
          ))}
          
          {/* Quick actions */}
          {showQuickActions && messages.length === 1 && !isLoading && !isStreaming && (
            <QuickActions onSelect={handleQuickAction} disabled={isLoading || isStreaming} />
          )}
          
          {/* V2.1: Service Explorer */}
          {showServiceExplorer && !isLoading && !isStreaming && (
            <ServiceExplorer onSelect={(msg) => { setShowServiceExplorer(false); handleQuickAction(msg); }} disabled={isLoading || isStreaming} />
          )}

          {/* Typing indicator */}
          {isLoading && !isStreaming && (
            <ChatMessage message={{ role: 'assistant', content: '' }} isTyping />
          )}

          {/* Lead capture form */}
          {showLeadCapture && !leadCaptured && (
            <LeadCaptureForm
              sessionId={sessionId}
              onSubmit={handleLeadSubmit}
              onCancel={() => setShowLeadCapture(false)}
            />
          )}

          {/* Success message */}
          {leadCaptured && showLeadCapture === false && messages[messages.length - 1]?.role === 'assistant' && (
            <LeadCaptureSuccess />
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="p-4 border-t border-white/[0.06] bg-gradient-to-r from-[#0A0F1A] to-[#0D1321] rounded-b-2xl">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about AI, software, or automation..."
                disabled={isLoading || isStreaming}
                className="w-full px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.08] text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-blue-500/40 focus:bg-white/[0.04] disabled:opacity-50 transition-all"
                aria-label="Type your message"
              />
            </div>
            <button
              onClick={() => sendMessageWithStreaming()}
              disabled={!inputValue.trim() || isLoading || isStreaming}
              className="w-11 h-11 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-white transition-all"
              aria-label="Send message"
            >
              {isLoading || isStreaming ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
          <p className="text-center text-gray-600 text-[10px] mt-2.5 flex items-center justify-center gap-1">
            <Zap className="w-2.5 h-2.5 text-blue-400" />
            <span className="text-gray-500">D3 AI</span>
          </p>
        </div>
      </div>
      
      {/* V2.1: Exit Intent Modal */}
      {showExitIntent && (
        <ExitIntentModal
          onYes={() => {
            setShowExitIntent(false);
            setShowLeadCapture(true);
          }}
          onMaybeLater={() => setShowExitIntent(false)}
        />
      )}
    </>
  );
};

export default ChatWidget;
